import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Check,
} from 'lucide-react';

type DriverBookingsCalendarPickerProps = {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
  initialFocusDate?: string;
};

// Helper: Format Date to YYYY-MM-DD (timezone-safe local date)
const toIsoDateString = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Helper: Parse YYYY-MM-DD into local Date
const parseIsoDate = (iso: string): Date | null => {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
};

// Helper: Human-friendly date string (e.g., "17 Jun 2026")
const formatDisplayDate = (iso: string): string => {
  const d = parseIsoDate(iso);
  if (!d) return iso;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const DriverBookingsCalendarPicker = ({
  startDate,
  endDate,
  onChange,
  initialFocusDate,
}: DriverBookingsCalendarPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Position coordinates for portal
  const [popoverCoords, setPopoverCoords] = useState<{
    top: number;
    left: number;
    placement: 'below' | 'above';
  }>({
    top: 0,
    left: 0,
    placement: 'below',
  });

  // Temporary selection inside popover before clicking Apply
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  // Calendar View Month & Year (0-indexed month)
  const [viewYear, setViewYear] = useState(() => {
    const focus = parseIsoDate(startDate) || parseIsoDate(initialFocusDate || '');
    return focus ? focus.getFullYear() : new Date().getFullYear();
  });

  const [viewMonth, setViewMonth] = useState(() => {
    const focus = parseIsoDate(startDate) || parseIsoDate(initialFocusDate || '');
    return focus ? focus.getMonth() : new Date().getMonth();
  });

  // Sync temp dates when props change or popover opens
  useEffect(() => {
    setTempStart(startDate);
    setTempEnd(endDate);
    if (startDate) {
      const parsed = parseIsoDate(startDate);
      if (parsed) {
        setViewYear(parsed.getFullYear());
        setViewMonth(parsed.getMonth());
      }
    } else if (initialFocusDate) {
      const parsed = parseIsoDate(initialFocusDate);
      if (parsed) {
        setViewYear(parsed.getFullYear());
        setViewMonth(parsed.getMonth());
      }
    }
  }, [startDate, endDate, initialFocusDate, isOpen]);

  const updatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 330;
    const popoverHeight = 390;
    const padding = 12;

    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: 'below' | 'above' =
      spaceBelow < popoverHeight && rect.top > popoverHeight
        ? 'above'
        : 'below';

    const top =
      placement === 'below'
        ? rect.bottom + 6
        : Math.max(padding, rect.top - popoverHeight - 6);

    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - padding) {
      left = Math.max(padding, window.innerWidth - popoverWidth - padding);
    }

    setPopoverCoords({ top, left, placement });
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      updatePopoverPosition();
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePopoverPosition();
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, updatePopoverPosition]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((prev) => prev - 1);
    } else {
      setViewMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((prev) => prev + 1);
    } else {
      setViewMonth((prev) => prev + 1);
    }
  };

  // Month grid generation
  const daysInMonth = useMemo(() => {
    const totalDays = new Date(viewYear, viewMonth + 1, 0).getDate();
    // Monday as 0: (d.getDay() + 6) % 7
    const firstDayOfWeek =
      (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
    const prevMonthTotalDays = new Date(viewYear, viewMonth, 0).getDate();

    const days: {
      dateStr: string;
      dayNumber: number;
      isCurrentMonth: boolean;
    }[] = [];

    // Leading days from previous month
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const dayNum = prevMonthTotalDays - i;
      const prevDate = new Date(viewYear, viewMonth - 1, dayNum);
      days.push({
        dateStr: toIsoDateString(prevDate),
        dayNumber: dayNum,
        isCurrentMonth: false,
      });
    }

    // Days in current month
    for (let i = 1; i <= totalDays; i++) {
      const currDate = new Date(viewYear, viewMonth, i);
      days.push({
        dateStr: toIsoDateString(currDate),
        dayNumber: i,
        isCurrentMonth: true,
      });
    }

    // Trailing days to complete the 7-column grid
    const remaining = (7 - (days.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const nextDate = new Date(viewYear, viewMonth + 1, i);
      days.push({
        dateStr: toIsoDateString(nextDate),
        dayNumber: i,
        isCurrentMonth: false,
      });
    }

    return days;
  }, [viewYear, viewMonth]);

  // Click on a day
  const handleDayClick = (dateStr: string) => {
    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(dateStr);
      setTempEnd('');
      setHoveredDate(null);
    } else if (tempStart && !tempEnd) {
      if (dateStr < tempStart) {
        setTempStart(dateStr);
        setTempEnd(tempStart);
      } else {
        setTempEnd(dateStr);
      }
      setHoveredDate(null);
    }
  };

  // Quick preset shortcuts
  const handleApplyPreset = (preset: 'today' | 'this-week' | 'this-month' | 'all') => {
    const now = new Date();
    if (preset === 'all') {
      setTempStart('');
      setTempEnd('');
      onChange('', '');
      setIsOpen(false);
      return;
    }

    if (preset === 'today') {
      const todayStr = toIsoDateString(now);
      setTempStart(todayStr);
      setTempEnd(todayStr);
      setViewYear(now.getFullYear());
      setViewMonth(now.getMonth());
      onChange(todayStr, todayStr);
      setIsOpen(false);
      return;
    }

    if (preset === 'this-week') {
      const day = (now.getDay() + 6) % 7;
      const monday = new Date(now);
      monday.setDate(now.getDate() - day);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const startStr = toIsoDateString(monday);
      const endStr = toIsoDateString(sunday);

      setTempStart(startStr);
      setTempEnd(endStr);
      setViewYear(monday.getFullYear());
      setViewMonth(monday.getMonth());
      onChange(startStr, endStr);
      setIsOpen(false);
      return;
    }

    if (preset === 'this-month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const startStr = toIsoDateString(start);
      const endStr = toIsoDateString(end);

      setTempStart(startStr);
      setTempEnd(endStr);
      setViewYear(now.getFullYear());
      setViewMonth(now.getMonth());
      onChange(startStr, endStr);
      setIsOpen(false);
      return;
    }
  };

  const handleApply = () => {
    if (tempStart && tempEnd) {
      if (tempStart <= tempEnd) {
        onChange(tempStart, tempEnd);
      } else {
        onChange(tempEnd, tempStart);
      }
    } else if (tempStart && !tempEnd) {
      onChange(tempStart, tempStart);
    } else {
      onChange('', '');
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempStart('');
    setTempEnd('');
    setHoveredDate(null);
    onChange('', '');
    setIsOpen(false);
  };

  const hasSelectedRange = Boolean(startDate || endDate);

  const triggerLabel = useMemo(() => {
    if (startDate && endDate) {
      if (startDate === endDate) {
        return formatDisplayDate(startDate);
      }
      return `${formatDisplayDate(startDate)} — ${formatDisplayDate(endDate)}`;
    }
    if (startDate) {
      return `From ${formatDisplayDate(startDate)}`;
    }
    if (endDate) {
      return `Until ${formatDisplayDate(endDate)}`;
    }
    return 'Filter by Date Range';
  }, [startDate, endDate]);

  const todayStr = useMemo(() => toIsoDateString(new Date()), []);

  return (
    <div className="relative inline-block">
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className={`flex h-[38px] items-center gap-2 rounded-full border px-3.5 font-host text-[13px] transition-all cursor-pointer shadow-2xs select-none ${
          hasSelectedRange || isOpen
            ? 'border-[#FF6A1A] bg-[#FFF8F3] text-black font-semibold ring-2 ring-[#FF6A1A]/10'
            : 'border-[#E0E0E0] bg-white text-[#555] hover:border-[#CCCCCC] hover:bg-[#FAFAFA]'
        }`}
        title="Open interactive calendar to pick date range"
        aria-expanded={isOpen}
      >
        <CalendarIcon
          size={15}
          className={hasSelectedRange || isOpen ? 'text-[#FF6A1A]' : 'text-[#757575]'}
        />

        <span className="whitespace-nowrap">{triggerLabel}</span>

        {hasSelectedRange ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleClear();
              }
            }}
            className="ml-0.5 rounded-full p-0.5 text-[#8A8A8A] hover:bg-black/10 hover:text-black transition-colors"
            title="Clear date range"
          >
            <X size={13} />
          </span>
        ) : (
          <ChevronDown
            size={14}
            className={`text-[#8A8A8A] transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-black' : ''
            }`}
          />
        )}
      </button>

      {/* Popover Calendar via Portal to document.body (completely prevents modal clipping/merging) */}
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            {/* Click-capture backdrop */}
            <div
              className="fixed inset-0 z-[99998] cursor-default bg-black/10 transition-opacity"
              onClick={() => setIsOpen(false)}
            />

            {/* Floating Calendar Card */}
            <div
              ref={popoverRef}
              style={{
                top: `${popoverCoords.top}px`,
                left: `${popoverCoords.left}px`,
                width: '330px',
              }}
              className="fixed z-[99999] rounded-[20px] border border-[#CCCCCC] bg-white p-4 text-left shadow-[0_25px_60px_-10px_rgba(0,0,0,0.35),0_0_0_1px_rgba(0,0,0,0.08)] ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-150"
            >
              {/* Quick Presets Bar */}
              <div className="mb-3 flex items-center justify-between gap-1 border-b border-[#F0F0F0] pb-3">
                <span className="font-host text-[11px] font-bold uppercase tracking-wider text-[#8A8A8A]">
                  Presets:
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleApplyPreset('today')}
                    className="rounded-full px-2.5 py-1 font-host text-[11px] font-medium text-gray-700 hover:bg-[#F0F0F0] transition-colors cursor-pointer"
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyPreset('this-week')}
                    className="rounded-full px-2.5 py-1 font-host text-[11px] font-medium text-gray-700 hover:bg-[#F0F0F0] transition-colors cursor-pointer"
                  >
                    This Week
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyPreset('this-month')}
                    className="rounded-full px-2.5 py-1 font-host text-[11px] font-medium text-gray-700 hover:bg-[#F0F0F0] transition-colors cursor-pointer"
                  >
                    This Month
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyPreset('all')}
                    className="rounded-full px-2.5 py-1 font-host text-[11px] font-medium text-[#FF6A1A] hover:bg-[#FFF0E6] transition-colors cursor-pointer"
                  >
                    All
                  </button>
                </div>
              </div>

              {/* Month & Year Navigation Header */}
              <div className="mb-3 flex items-center justify-between px-1">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-gray-600 hover:bg-[#F0F0F0] hover:text-black transition-colors cursor-pointer"
                  title="Previous month"
                >
                  <ChevronLeft size={16} />
                </button>

                <div className="font-host text-[14px] font-bold text-black select-none">
                  {MONTH_NAMES[viewMonth]} {viewYear}
                </div>

                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-gray-600 hover:bg-[#F0F0F0] hover:text-black transition-colors cursor-pointer"
                  title="Next month"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Day of Week Labels */}
              <div className="mb-1.5 grid grid-cols-7 text-center">
                {DAY_LABELS.map((day) => (
                  <span
                    key={day}
                    className="font-host text-[11px] font-bold uppercase text-[#9E9E9E] select-none"
                  >
                    {day}
                  </span>
                ))}
              </div>

              {/* Interactive Days Grid */}
              <div className="grid grid-cols-7 gap-y-1">
                {daysInMonth.map(({ dateStr, dayNumber, isCurrentMonth }) => {
                  const isStart = tempStart === dateStr;
                  const isEnd = tempEnd === dateStr;
                  const isSinglePicked = isStart && (isEnd || !tempEnd);

                  let isInRange = false;
                  if (tempStart && tempEnd) {
                    const s = tempStart < tempEnd ? tempStart : tempEnd;
                    const e = tempStart < tempEnd ? tempEnd : tempStart;
                    isInRange = dateStr >= s && dateStr <= e;
                  } else if (tempStart && !tempEnd && hoveredDate) {
                    const s = tempStart < hoveredDate ? tempStart : hoveredDate;
                    const e = tempStart < hoveredDate ? hoveredDate : tempStart;
                    isInRange = dateStr >= s && dateStr <= e;
                  }

                  const isToday = dateStr === todayStr;

                  return (
                    <div
                      key={dateStr}
                      className={`relative flex h-8 items-center justify-center p-0.5 ${
                        isInRange && !isSinglePicked
                          ? 'bg-[#FFF2EA]'
                          : ''
                      } ${isStart && !isEnd ? 'rounded-l-full' : ''} ${
                        isEnd && !isStart ? 'rounded-r-full' : ''
                      }`}
                      onMouseEnter={() => {
                        if (tempStart && !tempEnd) {
                          setHoveredDate(dateStr);
                        }
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => handleDayClick(dateStr)}
                        className={`flex h-7 w-7 items-center justify-center rounded-full font-host text-[12px] transition-all cursor-pointer select-none ${
                          isStart || isEnd
                            ? 'bg-[#FF6A1A] font-bold text-white shadow-xs'
                            : isInRange
                            ? 'font-bold text-[#FF6A1A] hover:bg-[#FF6A1A]/20'
                            : isCurrentMonth
                            ? 'text-black hover:bg-[#F0F0F0]'
                            : 'text-[#C4C4C4] hover:bg-[#F8F8F8]'
                        } ${isToday && !isStart && !isEnd ? 'ring-1 ring-[#FF6A1A]' : ''}`}
                      >
                        {dayNumber}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Selection Status & Action Footer */}
              <div className="mt-4 flex items-center justify-between border-t border-[#F0F0F0] pt-3">
                <div className="flex flex-col">
                  <span className="font-host text-[10px] uppercase font-bold text-[#8A8A8A]">
                    Range
                  </span>
                  <span className="font-host text-[11px] font-semibold text-black truncate max-w-[170px]">
                    {tempStart && tempEnd
                      ? `${formatDisplayDate(tempStart)} - ${formatDisplayDate(tempEnd)}`
                      : tempStart
                      ? `From ${formatDisplayDate(tempStart)}`
                      : 'No dates selected'}
                  </span>
                </div>

                <div className="flex items-center gap-1.5">
                  {(tempStart || tempEnd) && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="rounded-full px-2.5 py-1 font-host text-[12px] font-medium text-[#757575] hover:text-black transition-colors cursor-pointer"
                    >
                      Clear
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!tempStart}
                    className="flex items-center gap-1 rounded-full bg-[#1A1A1A] px-3.5 py-1.5 font-host text-[12px] font-semibold text-white transition-all hover:bg-[#333333] active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <Check size={13} />
                    <span>Apply</span>
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
